import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Alert,
  IconButton,
  Chip,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  WarningAmber as WarningAmberIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { getStayflexiHotelAddressData } from '../api/adminPanelApi';

/** Exact Bidflexi / GHA sample columns */
const BIDFLEXI_COLUMNS = [
  { key: 'id', label: 'id' },
  { key: 'name', label: 'name' },
  { key: 'addr1', label: 'addr1' },
  { key: 'city', label: 'city' },
  { key: 'province', label: 'province' },
  { key: 'postal_code', label: 'postal_code' },
  { key: 'country', label: 'country' },
  { key: 'latitude', label: 'latitude' },
  { key: 'longitude', label: 'longitude' },
  { key: 'hotel_brand', label: 'hotel_brand' },
  { key: 'alternate_hotel_id', label: 'alternate_hotel_id' },
];

const HOTEL_BRAND = 'tabtrips';
const ALTERNATE_HOTEL_ID = 'tabtrips.com';

const display = (v) => (v == null || v === '' ? '–' : String(v));

const ordinalDay = (n) => {
  const j = n % 10;
  const k = n % 100;
  if (j === 1 && k !== 11) return `${n}st`;
  if (j === 2 && k !== 12) return `${n}nd`;
  if (j === 3 && k !== 13) return `${n}rd`;
  return `${n}th`;
};

const exportFileNameToday = () => {
  const now = new Date();
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return `${ordinalDay(now.getDate())} ${months[now.getMonth()]} ${now.getFullYear()}.xlsx`;
};

/** Map API row → Bidflexi export / display row */
const toBidflexiRow = (apiRow) => ({
  id: apiRow.id ?? '',
  name: apiRow.name ?? '',
  addr1: apiRow.addr1 ?? '',
  city: apiRow.city ?? '',
  province: apiRow.province ?? '',
  postal_code: apiRow.postal_code ?? '',
  country: apiRow.country ?? '',
  latitude: apiRow.latitude ?? '',
  longitude: apiRow.longitude ?? '',
  hotel_brand: HOTEL_BRAND,
  alternate_hotel_id: ALTERNATE_HOTEL_ID,
  // internal only (not in excel)
  _hotel_id: apiRow.hotel_id,
  _rowKey: `${apiRow.hotel_id}-${apiRow.id ?? 'null'}`,
});

const StayflexiHotelAddressData = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('single');
  const [hotelId, setHotelId] = useState('');
  const [hotelIdFrom, setHotelIdFrom] = useState('');
  const [hotelIdTo, setHotelIdTo] = useState('');
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [hasFetched, setHasFetched] = useState(false);

  const unmappedCount = useMemo(
    () => rows.filter((r) => r.id == null || r.id === '').length,
    [rows]
  );

  const validate = () => {
    if (mode === 'single') {
      const id = Number(String(hotelId).trim());
      if (!String(hotelId).trim() || Number.isNaN(id) || id <= 0) {
        return 'Enter a valid hotel_id';
      }
      return null;
    }
    const from = Number(String(hotelIdFrom).trim());
    const to = Number(String(hotelIdTo).trim());
    if (!String(hotelIdFrom).trim() || Number.isNaN(from) || from <= 0) {
      return 'Enter a valid hotel_id_from';
    }
    if (!String(hotelIdTo).trim() || Number.isNaN(to) || to <= 0) {
      return 'Enter a valid hotel_id_to';
    }
    if (from > to) {
      return 'hotel_id_from cannot be greater than hotel_id_to';
    }
    return null;
  };

  const handleFetch = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      setSnackbar({ open: true, message: validationError, severity: 'warning' });
      return;
    }

    setLoading(true);
    setError('');
    setInfo('');
    setHasFetched(true);
    try {
      const params =
        mode === 'single'
          ? { hotel_id: Number(String(hotelId).trim()) }
          : {
              hotel_id_from: Number(String(hotelIdFrom).trim()),
              hotel_id_to: Number(String(hotelIdTo).trim()),
            };

      const res = await getStayflexiHotelAddressData(params);
      if (!res.success) {
        setRows([]);
        setFilters(null);
        setError(res.error || 'Failed to load data');
        setSnackbar({ open: true, message: res.error || 'Failed to load data', severity: 'error' });
        return;
      }

      const mapped = (res.hotels || []).map(toBidflexiRow);
      setRows(mapped);
      setFilters(res.filters || null);
      setInfo(res.message || '');
      if (!mapped.length) {
        setSnackbar({
          open: true,
          message: res.message || 'No hotels returned for this filter',
          severity: 'info',
        });
      }
    } catch (e) {
      setRows([]);
      setError(e.message || 'Failed to load data');
      setSnackbar({ open: true, message: e.message || 'Failed to load data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveRow = (rowKey) => {
    setRows((prev) => prev.filter((r) => r._rowKey !== rowKey));
  };

  const handleExportExcel = () => {
    if (!rows.length) {
      setSnackbar({ open: true, message: 'Nothing to export — fetch data first', severity: 'warning' });
      return;
    }

    const exportRows = rows.map((r) => ({
      id: r.id,
      name: r.name,
      addr1: r.addr1,
      city: r.city,
      province: r.province,
      postal_code: r.postal_code,
      country: r.country,
      latitude: r.latitude,
      longitude: r.longitude,
      hotel_brand: HOTEL_BRAND,
      alternate_hotel_id: ALTERNATE_HOTEL_ID,
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportRows, {
      header: BIDFLEXI_COLUMNS.map((c) => c.key),
    });
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    const fileName = exportFileNameToday();
    XLSX.writeFile(wb, fileName);
    setSnackbar({ open: true, message: `Downloaded ${fileName}`, severity: 'success' });
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') handleFetch();
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
      {loading && (
        <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1200 }} />
      )}

      <Box sx={{ maxWidth: 1480, mx: 'auto' }}>
        <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <IconButton
            onClick={() => navigate('/')}
            size="small"
            sx={{
              color: '#FF7700',
              bgcolor: '#fff',
              border: '1px solid #e4e8ee',
              '&:hover': { bgcolor: '#fff5eb' },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="h5" fontWeight={800} sx={{ color: '#2E4057', letterSpacing: '-0.02em' }}>
              Stayflexi Hotel Address Data
            </Typography>
            <Typography variant="body2" color="text.secondary">
              GHA Excel · hotel_brand = tabtrips · alternate_hotel_id = tabtrips.com
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
            onClick={handleFetch}
            disabled={loading || !hasFetched}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Refresh data
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleExportExcel}
            disabled={!rows.length || loading}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: '#2e7d32',
              '&:hover': { bgcolor: '#1b5e20' },
            }}
          >
            Export Excel
          </Button>
          {hasFetched && (
            <Chip
              label={`${rows.length} row${rows.length === 1 ? '' : 's'}`}
              sx={{ fontWeight: 700, bgcolor: '#fff4e8', color: '#a84d00' }}
            />
          )}
          {unmappedCount > 0 && (
            <Chip
              icon={<WarningAmberIcon />}
              label={`${unmappedCount} missing Stayflexi id`}
              color="warning"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>

        <Paper
          sx={{
            p: { xs: 2, md: 2.5 },
            mb: 2,
            borderRadius: 2.5,
            border: '1px solid #e4e8ee',
            borderLeft: '4px solid #FF7700',
            boxShadow: '0 4px 16px rgba(46, 64, 87, 0.06)',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md="auto">
              <ToggleButtonGroup
                exclusive
                size="small"
                value={mode}
                onChange={(_, v) => v && setMode(v)}
                sx={{
                  '& .MuiToggleButton-root': {
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 2,
                  },
                  '& .Mui-selected': {
                    bgcolor: '#fff1e5 !important',
                    color: '#c45d00 !important',
                  },
                }}
              >
                <ToggleButton value="single">Single</ToggleButton>
                <ToggleButton value="range">Range</ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            {mode === 'single' ? (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="hotel_id"
                  placeholder="50050200"
                  value={hotelId}
                  onChange={(e) => setHotelId(e.target.value)}
                  onKeyDown={onKeyDown}
                  disabled={loading}
                />
              </Grid>
            ) : (
              <>
                <Grid item xs={6} sm={4} md={2.5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="hotel_id_from"
                    placeholder="50050200"
                    value={hotelIdFrom}
                    onChange={(e) => setHotelIdFrom(e.target.value)}
                    onKeyDown={onKeyDown}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2.5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="hotel_id_to"
                    placeholder="50050256"
                    value={hotelIdTo}
                    onChange={(e) => setHotelIdTo(e.target.value)}
                    onKeyDown={onKeyDown}
                    disabled={loading}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} sm={4} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                onClick={handleFetch}
                disabled={loading}
                sx={{
                  height: 40,
                  bgcolor: '#FF7700',
                  '&:hover': { bgcolor: '#e65100' },
                  boxShadow: '0 4px 10px rgba(255, 119, 0, 0.25)',
                }}
              >
                {loading ? 'Fetching…' : 'Fetch'}
              </Button>
            </Grid>
          </Grid>

          {filters && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              Filters: {typeof filters === 'object' ? JSON.stringify(filters) : String(filters)}
            </Typography>
          )}
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {!error && info && hasFetched && rows.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {info}
          </Alert>
        )}

        <Paper sx={{ borderRadius: 2.5, overflow: 'hidden', border: '1px solid #e4e8ee' }}>
          <TableContainer sx={{ maxHeight: 680 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      bgcolor: '#f5f7fa',
                      width: 28,
                      minWidth: 28,
                      maxWidth: 28,
                      p: 0,
                      textAlign: 'center',
                    }}
                  />
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                    hotel_id
                  </TableCell>
                  {BIDFLEXI_COLUMNS.map((col) => (
                    <TableCell
                      key={col.key}
                      sx={{ fontWeight: 700, bgcolor: '#f5f7fa', whiteSpace: 'nowrap', fontSize: '0.75rem' }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {!hasFetched && !loading && (
                  <TableRow>
                    <TableCell colSpan={BIDFLEXI_COLUMNS.length + 2} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">
                        Choose Single or Range, enter hotel id(s), then Fetch.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {hasFetched && !loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={BIDFLEXI_COLUMNS.length + 2} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">
                        {info || error || 'No rows returned.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row) => {
                  const missingSf = row.id == null || row.id === '';
                  return (
                    <TableRow
                      key={row._rowKey}
                      hover
                      sx={
                        missingSf
                          ? { bgcolor: '#fff8e1', '&:hover': { bgcolor: '#ffecb3' } }
                          : undefined
                      }
                    >
                      <TableCell
                        sx={{
                          width: 28,
                          minWidth: 28,
                          maxWidth: 28,
                          p: 0,
                          textAlign: 'center',
                          verticalAlign: 'middle',
                        }}
                      >
                        <Tooltip title="Remove from list">
                          <Box
                            component="button"
                            type="button"
                            onClick={() => handleRemoveRow(row._rowKey)}
                            aria-label="Remove hotel"
                            sx={{
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              color: '#d32f2f',
                              fontSize: 14,
                              fontWeight: 700,
                              lineHeight: 1,
                              p: 0,
                              m: 0,
                              width: 18,
                              height: 18,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              '&:hover': { color: '#b71c1c', bgcolor: '#ffebee', borderRadius: '2px' },
                            }}
                          >
                            ×
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                        {display(row._hotel_id)}
                      </TableCell>
                      {BIDFLEXI_COLUMNS.map((col) => (
                        <TableCell
                          key={col.key}
                          sx={{
                            fontSize: '0.78rem',
                            maxWidth: col.key === 'addr1' || col.key === 'name' ? 200 : undefined,
                            whiteSpace: col.key === 'addr1' || col.key === 'name' ? 'normal' : 'nowrap',
                          }}
                        >
                          {col.key === 'id' && missingSf ? (
                            <Tooltip title="No Stayflexi mapping">
                              <Chip
                                size="small"
                                label="null"
                                color="warning"
                                variant="outlined"
                                sx={{ height: 20, fontSize: 11 }}
                              />
                            </Tooltip>
                          ) : (
                            display(row[col.key])
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StayflexiHotelAddressData;
